import {
	ChangeEvent,
	Dispatch,
	FunctionComponent,
	SetStateAction,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
	faChevronDown,
	faChevronRight,
	faSearch,
	faLink,
	faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";
import { Spinner } from "phosphor-react";

import { classNames } from "@/utils/classNames";
import { useSearch } from "@/utils";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { clearGlobalSearch, searchSelector } from "@/pages/Private/redux/search/search.slice";
import { useLazyGlobalSearchQuery } from "@/pages/Private/redux/search/search.api";
import { CompanyEnrichPreviewPerson } from "@/pages/Private/pages/CompanyEnrich/schema/companyEnrich";
import { SENIORITY_OPTIONS } from "@/pages/Private/pages/CompanyEnrich/constants/seniority.constants";
import { ResolvedLinkedinProspect } from "@/pages/Private/pages/List/schema/list";

import { Icon } from "../Icon/Icon";
import { Checkbox } from "../Checkbox/Checkbox";
import { SearchField } from "../SearchField/SearchField";
import { LinkedinUrlChipsInput } from "./LinkedinUrlChipsInput";
import { ResolvedProspectCard } from "./ResolvedProspectCard";
import { useResolveLinkedinUrls } from "./useResolveLinkedinUrls";
import { hasDisplayableCompany, ParsedLinkedinUrl } from "./utils";
import { Loader } from "../Loader/Loader";

interface SelectedLinkedInItem {
	linkedinUrl: string;
	name: string;
	domain?: string;
	companyEnrichId?: number;
	// Set for prospects resolved from a pasted LinkedIn URL — added to the list synchronously
	// by prospectId (see GlobalSearchModal.handleAddToList).
	prospectId?: number;
	image?: string;
}

interface ByUrlTabsProps {
	selectedLinkedInItems: SelectedLinkedInItem[];
	setSelectedLinkedInItems: Dispatch<SetStateAction<SelectedLinkedInItem[]>>;
	// Resolved prospects already in the company are routed here (added by prospectId, no credit) —
	// same bucket the other tabs use for existing contacts.
	selectedContactIds: number[];
	setSelectedContactIds: Dispatch<SetStateAction<number[]>>;
	// Incremented by the parent after a successful "Add to list" — clears the pasted chips.
	resetSignal: number;
	selectedIds: number[];
	// Reports footer-relevant state up to the modal: whether any chip is invalid (to block
	// "Add to list"), and the counts used for the footer summary line.
	onFooterStatusChange: (status: ByUrlFooterStatus) => void;
	// Single-select (compose) mode: disables auto-selecting every resolved URL and hides the
	// "Select all" control — the user picks exactly one resolved card / result.
	singleSelect?: boolean;
}

export interface ByUrlFooterStatus {
	hasInvalidChips: boolean;
	// Profiles successfully resolved from pasted URLs (URL mode).
	resolvedCount: number;
	// LinkedIn results returned by the name search (name mode).
	nameResultsCount: number;
}

const NAME_MIN_CHARS = 2;

const formatSlug = (slug: string) =>
	slug
		.split("-")
		.map((w) => w.charAt(0).toUpperCase() + w.slice(1))
		.join(" ");

const formatDepartmentLabel = (id: string) => id.split("/").map(formatSlug).join(" › ");

const SENIORITY_LABEL_MAP = new Map(SENIORITY_OPTIONS.map((o) => [o.id, o.title]));
const formatSeniorityLabel = (id: string) => SENIORITY_LABEL_MAP.get(id) ?? formatSlug(id);

export const ByUrlTab: FunctionComponent<ByUrlTabsProps> = ({
	selectedLinkedInItems,
	setSelectedLinkedInItems,
	selectedContactIds,
	setSelectedContactIds,
	resetSignal,
	selectedIds,
	onFooterStatusChange,
	singleSelect = false,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`companyEnrich.${key}`);
	const dispatch = useAppDispatch();

	// ── Input state ───────────────────────────────────────────────────────────
	// nameQuery is the immediate input value; nameQueryDebounced is the 800ms-debounced
	// value from useSearch that actually drives the search (same as the By-name tab).
	const {
		searchValue: nameQuery,
		setSearchValue: setNameQuery,
		query: nameQueryDebounced,
		setQuery: setNameQueryDebounced,
	} = useSearch();
	const [urlChips, setUrlChips] = useState<ParsedLinkedinUrl[]>([]);
	const [isLinkedInExpanded, setIsLinkedInExpanded] = useState(true);
	const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

	// ── Results state ───────────────────────────────────────────────────────────
	const { companyEnrich } = useAppSelector(searchSelector);
	const linkedInResults = companyEnrich.items;
	const [fetchSearch, { isFetching }] = useLazyGlobalSearchQuery();

	// Only one input is active at a time: pasting URLs disables name search and
	// vice-versa, mirroring the prototype's mutually-exclusive blocks.
	const mode: "name" | "url" | "none" =
		urlChips.length > 0 ? "url" : nameQuery.trim() ? "name" : "none";

	// Searching = a fetch is in flight, OR the typed name hasn't settled into the debounced
	// value yet (the keystroke→debounce window). The fetch itself is dispatched in a layout
	// effect below, so isFetching flips before paint — together this keeps a continuous
	// spinner and never flashes "0 results" before the current input's results land.
	const trimmedName = nameQuery.trim();
	const isSearching =
		isFetching ||
		(mode === "name" &&
			trimmedName.length >= NAME_MIN_CHARS &&
			trimmedName !== nameQueryDebounced.trim());

	const validUrlCount = urlChips.filter((c) => c.valid).length;

	// Debounced name search — reuses the same globalSearch the By-name tab uses, which
	// populates companyEnrich in the slice. The debounce comes from useSearch's `query`
	// (800ms). Dispatched in a layout effect so isFetching turns on before the browser paints
	// the settle frame — otherwise there's a one-frame flash of "no results" before the spinner.
	useLayoutEffect(() => {
		if (mode === "url") {
			return;
		}

		const q = nameQueryDebounced.trim();

		if (q.length < NAME_MIN_CHARS) {
			dispatch(clearGlobalSearch());

			return;
		}

		fetchSearch({ personSearchSource: { query: q } });
	}, [nameQueryDebounced, mode, fetchSearch, dispatch]);

	// ── URL resolution ───────────────────────────────────────────────────────────
	// Each valid pasted URL is resolved into a saved prospect (one request per URL).
	const validUrls = useMemo(() => urlChips.filter((c) => c.valid).map((c) => c.url), [urlChips]);
	const resolutions = useResolveLinkedinUrls(validUrls);

	const resolvedCount = useMemo(
		() => Object.values(resolutions).filter((r) => r.status === "resolved").length,
		[resolutions]
	);

	// Surface footer-relevant state to the parent (invalid chips block "Add to list"; the counts
	// drive the footer summary line). Reset on unmount so switching tabs clears it.
	useEffect(() => {
		onFooterStatusChange({
			hasInvalidChips: urlChips.some((c) => !c.valid),
			resolvedCount,
			nameResultsCount: linkedInResults.length,
		});

		return () =>
			onFooterStatusChange({ hasInvalidChips: false, resolvedCount: 0, nameResultsCount: 0 });
	}, [urlChips, resolvedCount, linkedInResults.length, onFooterStatusChange]);

	// prospectIds we've already auto-selected, so a user's manual deselect isn't undone.
	const autoSelectedRef = useRef<Set<number>>(new Set());

	// Auto-select each newly-resolved prospect (mirrors the "pasted = ready to add" UX),
	// keyed by its prospectId. Prospects already in the company go into the existing-contacts
	// bucket (no credit); new ones into the LinkedIn bucket. The user can still deselect cards.
	useEffect(() => {
		// Single-select mode: don't auto-select resolved URLs — the user explicitly picks one.
		if (singleSelect) {
			return;
		}

		const fresh = Object.values(resolutions)
			.filter((r) => r.status === "resolved" && r.prospect?.id != null)
			.map((r) => r.prospect!)
			.filter((p) => !autoSelectedRef.current.has(p.id!));

		if (fresh.length === 0) {
			return;
		}

		fresh.forEach((p) => autoSelectedRef.current.add(p.id!));

		const existing = fresh.filter((p) => p.isExistingContact).map((p) => p.id!);
		const fresh_new = fresh.filter((p) => !p.isExistingContact);

		if (existing.length > 0) {
			setSelectedContactIds((prev) => [...prev, ...existing.filter((id) => !prev.includes(id))]);
		}

		if (fresh_new.length > 0) {
			setSelectedLinkedInItems((prev) => [
				...prev,
				...fresh_new.map((p) => ({
					prospectId: p.id!,
					name: p.name ?? "",
					linkedinUrl: p.linkedinUrl ?? "",
					image: p.image ?? undefined,
				})),
			]);
		}
	}, [resolutions, setSelectedLinkedInItems, setSelectedContactIds, singleSelect]);

	// Reset the pasted chips after a successful add (driven by the parent's explicit signal, not
	// by the selection going empty — otherwise deselecting the last card would wipe the chips).
	// Skips the initial mount.
	const didMountRef = useRef(false);

	useEffect(() => {
		if (!didMountRef.current) {
			didMountRef.current = true;

			return;
		}

		setUrlChips([]);
		autoSelectedRef.current = new Set();
	}, [resetSignal]);

	const getMatchedExperience = useCallback(
		(row: CompanyEnrichPreviewPerson) =>
			row.experiences?.find((exp) => exp.is_matched && exp.company?.id) ?? null,
		[]
	);

	const getCurrentExperience = useCallback(
		(row: CompanyEnrichPreviewPerson) =>
			row.experiences?.find((exp) => exp.is_current && exp.company?.id) ?? null,
		[]
	);

	const selectableLinkedIn = useMemo(
		() => linkedInResults.filter(hasDisplayableCompany),
		[linkedInResults]
	);

	const isSelectedAllLinkedIn =
		selectableLinkedIn.length > 0 &&
		selectableLinkedIn.every((p) =>
			selectedLinkedInItems.some((li) => li.companyEnrichId === p.id)
		);

	// ── Selection sync ──────────────────────────────────────────────────────────
	// Chips only drive resolution; the resolved prospects (keyed by prospectId) are what
	// gets selected and added — see the auto-select effect and toggleResolved below.
	const handleUrlChipsChange = (next: ParsedLinkedinUrl[]) => {
		setUrlChips(next);
	};

	// Whether a resolved prospect is currently selected — existing contacts live in the
	// contacts bucket, new ones in the LinkedIn bucket.
	const isResolvedSelected = (prospect: ResolvedLinkedinProspect): boolean =>
		prospect.isExistingContact
			? prospect.id != null && selectedContactIds.includes(prospect.id)
			: selectedLinkedInItems.some((li) => li.prospectId === prospect.id);

	// Toggle a single resolved prospect in/out of the shared selection (correct bucket).
	const toggleResolved = (prospect: ResolvedLinkedinProspect) => {
		const id = prospect.id;

		if (id == null) {
			return;
		}

		if (prospect.isExistingContact) {
			setSelectedContactIds((prev) =>
				prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
			);

			return;
		}

		setSelectedLinkedInItems((prev) =>
			prev.some((li) => li.prospectId === id)
				? prev.filter((li) => li.prospectId !== id)
				: [
						...prev,
						{
							prospectId: id,
							name: prospect.name ?? "",
							linkedinUrl: prospect.linkedinUrl ?? "",
							image: prospect.image ?? undefined,
						},
					]
		);
	};

	const switchToName = () => {
		setUrlChips([]);

		const autoSelected = autoSelectedRef.current;

		setSelectedContactIds((prev) => prev.filter((id) => !autoSelected.has(id)));
		setSelectedLinkedInItems((prev) => prev.filter((li) => li.prospectId === undefined));
		autoSelectedRef.current = new Set();
	};

	const switchToUrl = () => {
		setNameQuery("");
		setNameQueryDebounced("");
		dispatch(clearGlobalSearch());
		setSelectedLinkedInItems(
			selectedLinkedInItems.filter((li) => li.companyEnrichId === undefined)
		);
	};

	const toggleLinkedInPerson = (person: CompanyEnrichPreviewPerson) => {
		const isSelected = selectedLinkedInItems.some((li) => li.companyEnrichId === person.id);

		if (isSelected) {
			setSelectedLinkedInItems(
				selectedLinkedInItems.filter((li) => li.companyEnrichId !== person.id)
			);

			return;
		}

		const company = (getMatchedExperience(person) ?? getCurrentExperience(person))?.company;

		setSelectedLinkedInItems([
			...selectedLinkedInItems,
			{
				companyEnrichId: person.id,
				name: person.name,
				linkedinUrl: "",
				domain: company?.domain ?? "",
			},
		]);
	};

	const toggleSelectAllLinkedIn = () => {
		if (isSelectedAllLinkedIn) {
			const currentIds = new Set(selectableLinkedIn.map((p) => p.id));

			setSelectedLinkedInItems(
				selectedLinkedInItems.filter((li) => !currentIds.has(li.companyEnrichId ?? -1))
			);

			return;
		}

		const existingIds = new Set(selectedLinkedInItems.map((li) => li.companyEnrichId));
		const toAdd: SelectedLinkedInItem[] = selectableLinkedIn
			.filter((person) => !existingIds.has(person.id))
			.map((person) => {
				const company = (getMatchedExperience(person) ?? getCurrentExperience(person))?.company;

				return {
					companyEnrichId: person.id,
					name: person.name,
					linkedinUrl: "",
					domain: company?.domain ?? "",
				};
			});

		setSelectedLinkedInItems([...selectedLinkedInItems, ...toAdd]);
	};

	// ── Disabled-mode note (mutual exclusivity) ─────────────────────────────────
	const disabledNote = (text: string, label: string, onSwitch: () => void) => (
		// pointer-events-auto so the switch link stays clickable even when the
		// surrounding block is dimmed with pointer-events-none.
		<div className="flex items-center flex-wrap gap-1.5 mt-2 text-xs text-gray-500 pointer-events-auto">
			<span>{text}</span>
			<button
				className="text-blue-action font-semibold underline hover:cursor-pointer"
				type="button"
				onClick={onSwitch}
			>
				{label}
			</button>
		</div>
	);

	// ── Right pane: LinkedIn results (name mode) ────────────────────────────────
	const linkedinSection = (
		<section>
			<div className="flex items-center mb-3 w-full justify-between">
				<button
					className="flex gap-1 items-center"
					onClick={() => setIsLinkedInExpanded((p) => !p)}
				>
					<Icon
						className="text-gray-600 text-[10px]"
						icon={isLinkedInExpanded ? faChevronDown : faChevronRight}
					/>
					<span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
						From LinkedIn
					</span>
					{!isSearching && linkedInResults.length > 0 && (
						<span className="text-[10px] font-semibold text-gray-700 bg-gray-100 px-2 rounded-full">
							{linkedInResults.length}
						</span>
					)}
				</button>
				{!singleSelect && selectableLinkedIn.length > 0 && (
					<button
						className="w-fit whitespace-nowrap text-blue-action text-[11px] hover:cursor-pointer hover:underline"
						onClick={toggleSelectAllLinkedIn}
					>
						{isSelectedAllLinkedIn ? "Deselect all" : "Select all"}
					</button>
				)}
			</div>

			{isLinkedInExpanded && (
				<>
					{isSearching && (
						<div className="flex justify-center py-4">
							<Loader />{" "}
						</div>
					)}
					{!isSearching &&
						selectableLinkedIn.map((person, idx) => {
							const isSelected = selectedIds.includes(person.id);
							const company = (getMatchedExperience(person) ?? getCurrentExperience(person))
								?.company;

							if (!company) {
								return null;
							}

							return (
								<div
									key={person.id ?? idx}
									className="flex w-full gap-2 border py-2.5 px-2 rounded-md border-gray-200 mb-4 last:mb-0 cursor-pointer"
									onClick={() => toggleLinkedInPerson(person)}
								>
									<Checkbox className="w-fit!" isChecked={isSelected} />
									<div className="flex items-center gap-3 py-2.5 px-1">
										{person.image_url && !failedImages.has(person.image_url) ? (
											<img
												alt={person.name ?? ""}
												className="w-8 h-8 object-cover rounded-full shrink-0"
												src={person.image_url}
												onError={() =>
													setFailedImages((prev) => new Set([...prev, person.image_url!]))
												}
											/>
										) : (
											<div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-semibold text-gray-600 shrink-0">
												{person.name?.charAt(0)?.toUpperCase() || "?"}
											</div>
										)}

										<div className="flex flex-col min-w-0">
											<span className="text-sm font-semibold text-gray-900">{person.name}</span>
											<div className="flex items-center gap-1.5 mt-0.5">
												{company?.logo_url && (
													<img
														alt={company.name ?? "company-logo"}
														className="w-4 h-4 rounded object-cover shrink-0"
														src={company.logo_url}
														onError={() =>
															setFailedImages((prev) => new Set([...prev, company.logo_url!]))
														}
													/>
												)}
												<span className="text-xs text-gray-500 truncate">{company?.name}</span>
											</div>
											{person.position && (
												<span className="text-xs text-gray-600">{person.position}</span>
											)}

											{(person.seniority || person.department) && (
												<div className="flex flex-row flex-wrap gap-1.5 mt-0.5">
													{person.seniority && (
														<span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-800">
															{formatSeniorityLabel(person.seniority)}
														</span>
													)}
													{person.department && (
														<span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-light-blue text-blue-action">
															{formatDepartmentLabel(person.department)}
														</span>
													)}
												</div>
											)}
										</div>
									</div>
								</div>
							);
						})}
					{!isSearching && linkedInResults.length === 0 && (
						<p className="text-xs italic text-gray-500 py-1">{t("basics.noResults")}</p>
					)}
				</>
			)}
		</section>
	);

	// ── Right pane: resolved profiles (URL mode) ────────────────────────────────
	// Renders one row per valid chip: a spinner while resolving, an inline error when the URL
	// couldn't be resolved, or the resolved card once it lands.
	const renderChipRow = (chip: ParsedLinkedinUrl) => {
		const resolution = resolutions[chip.url];

		if (!resolution || resolution.status === "loading") {
			return (
				<div
					key={chip.url}
					className="w-full border border-gray-200 rounded-xl flex items-center gap-3 p-3"
				>
					<Spinner className="animate-spin shrink-0" color="#9CA3AF" />
					<div className="flex flex-col min-w-0">
						<span className="text-sm text-gray-700 truncate">{chip.handle}</span>
						<span className="text-xs text-gray-400">{ts("byUrlResolving")}</span>
					</div>
				</div>
			);
		}

		if (resolution.status === "failed") {
			return (
				<div
					key={chip.url}
					className="w-full border border-status-error/40 bg-status-error-light/40 rounded-xl flex items-center gap-3 p-3"
				>
					<Icon className="text-status-error shrink-0" icon={faTriangleExclamation} />
					<div className="flex flex-col min-w-0">
						<span className="text-sm text-gray-700 truncate">{chip.handle}</span>
						<span className="text-xs text-status-error truncate">
							{resolution.reason || ts("byUrlResolveFailed")}
						</span>
					</div>
				</div>
			);
		}

		const prospect = resolution.prospect!;

		return (
			<ResolvedProspectCard
				key={chip.url}
				prospect={prospect}
				isSelected={isResolvedSelected(prospect)}
				failedImages={failedImages}
				onSelect={() => toggleResolved(prospect)}
				onImageError={(url) => setFailedImages((prev) => new Set([...prev, url]))}
			/>
		);
	};

	const sectionHeader = (label: string, count: number) => (
		<div className="flex items-center gap-2 mb-1">
			<span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
				{label}
			</span>
			<span className="text-[10px] font-semibold text-gray-700 bg-gray-100 px-2 rounded-full">
				{count}
			</span>
		</div>
	);

	// Already-in-company prospects show under "Existing contacts"; everything else (resolving,
	// failed, or newly-resolved) under "From LinkedIn URLs".
	const validChips = urlChips.filter((c) => c.valid);
	const existingChips = validChips.filter((c) => {
		const r = resolutions[c.url];

		return r?.status === "resolved" && Boolean(r.prospect?.isExistingContact);
	});
	const linkedinChips = validChips.filter((c) => !existingChips.includes(c));

	const urlResultsSection = (
		<div className="p-4 flex flex-col gap-5">
			{existingChips.length > 0 && (
				<section className="flex flex-col gap-3">
					{sectionHeader(ts("byUrlExistingHeader"), existingChips.length)}
					{existingChips.map(renderChipRow)}
				</section>
			)}

			{linkedinChips.length > 0 && (
				<section className="flex flex-col gap-3">
					{sectionHeader(ts("byUrlResolvedHeader"), linkedinChips.length)}
					{linkedinChips.map(renderChipRow)}
				</section>
			)}
		</div>
	);

	// ── Right pane: empty / URL summary states ──────────────────────────────────
	const rightPlaceholder = (title: string, description: string) => (
		<div className="flex-1 flex flex-col items-center pt-8 gap-2 px-6 pb-8 text-center">
			<div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center">
				<Icon className="text-gray-500 text-base" icon={mode === "url" ? faLink : faSearch} />
			</div>
			<p className="text-sm font-medium text-gray-700">{title}</p>
			<p className="text-xs text-gray-500 leading-relaxed w-[300px]">{description}</p>
		</div>
	);

	return (
		<div className="flex min-h-[320px] h-[480px] max-h-[480px]">
			{/* ── Left column — inputs ── */}
			<div className="w-[320px] shrink-0 border-r border-gray-300 flex flex-col">
				<div className="flex-1 overflow-y-auto bb-scrollbar-darker p-4 flex flex-col gap-4">
					{/* Search LinkedIn by name */}
					<div className={classNames(mode === "url" && "opacity-50 pointer-events-none")}>
						<label className="block text-sm font-medium text-gray-900 mb-2">
							{ts("byUrlNameSearchLabel")}
						</label>
						<SearchField
							handleChange={(e: ChangeEvent<HTMLInputElement>) => setNameQuery(e.target.value)}
							handleReset={() => {
								setNameQuery("");
								setNameQueryDebounced("");
								dispatch(clearGlobalSearch());
							}}
							icon={<Icon className="text-gray-500" icon={faSearch} />}
							iconPosition="left"
							classNameInput="max-w-[300px]!"
							name="linkedinNameSearch"
							placeholder={ts("byUrlNameSearchPlaceholder")}
							value={nameQuery}
						/>
						<p className="text-xs text-gray-500 mt-1.5 leading-relaxed">
							{ts("byUrlNameSearchHelper")}
						</p>
						{mode === "url" &&
							disabledNote(ts("byUrlNameDisabledNote"), ts("byUrlSwitchToName"), switchToName)}
					</div>

					{/* or */}
					<div className="flex items-center gap-2.5 text-gray-400 text-xs">
						<span className="flex-1 h-px bg-gray-300" />
						<span>{ts("byUrlOr")}</span>
						<span className="flex-1 h-px bg-gray-300" />
					</div>

					{/* Paste LinkedIn URLs */}
					<div>
						<LinkedinUrlChipsInput
							chips={urlChips}
							disabled={mode === "name"}
							onChange={handleUrlChipsChange}
						/>
						{mode === "name" &&
							disabledNote(ts("byUrlUrlDisabledNote"), ts("byUrlSwitchToUrl"), switchToUrl)}
					</div>
				</div>
			</div>

			{/* ── Right column — results / summary ── */}
			<div className="flex-1 flex flex-col overflow-y-auto bb-scrollbar-darker">
				{mode === "name" && <div className="p-4">{linkedinSection}</div>}
				{mode === "url" &&
					(validUrlCount > 0
						? urlResultsSection
						: rightPlaceholder(ts("byUrlEmptyTitle"), ts("byUrlReadyDescription")))}
				{mode === "none" && rightPlaceholder(ts("byUrlEmptyTitle"), ts("byUrlEmptyDescription"))}
			</div>
		</div>
	);
};
